import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Trophy, X } from "lucide-react";
import { successStories } from "@/data/successStories";
import { PATREON_LINK } from "@/constants";

const SuccessStoriesSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % successStories.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + successStories.length) % successStories.length);
  };

  const openModal = (story) => {
    setModalImage(story);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImage(null);
  };

  return (
    <section
      id="success-stories"
      data-testid="success-stories-section"
      className="py-20 md:py-32 bg-ics-navy-mid"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="section-divider mx-auto" />
          <div className="flex items-center justify-center gap-3 mb-4">
            <Trophy className="w-8 h-8 text-ics-orange" />
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-ics-white">
              Success Stories
            </h2>
          </div>
          <p className="text-ics-gray max-w-2xl mx-auto">
            Real results from our community. Proof of timing, discipline, and Shariah-compliant investing.
          </p>
        </div>

        {/* Desktop Grid View */}
        <div className="hidden md:grid md:grid-cols-3 gap-6 lg:gap-8">
          {successStories.map((story, index) => (
            <div
              key={story.id}
              data-testid={`success-story-card-${index}`}
              className="glass-card rounded-2xl overflow-hidden hover-lift cursor-pointer group"
              onClick={() => openModal(story)}
            >
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={story.imageUrl}
                  alt={story.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ics-navy/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white text-sm font-medium">{story.title}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Carousel View */}
        <div className="md:hidden relative">
          <div className="overflow-hidden rounded-2xl">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {successStories.map((story, index) => (
                <div
                  key={story.id}
                  className="w-full flex-shrink-0 px-2"
                  onClick={() => openModal(story)}
                >
                  <div className="glass-card rounded-2xl overflow-hidden">
                    <img
                      src={story.imageUrl}
                      alt={story.title}
                      className="w-full aspect-square object-cover"
                    />
                    <div className="p-4">
                      <h3 className="text-ics-white font-medium text-sm">{story.title}</h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Carousel Controls */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={prevSlide}
              data-testid="carousel-prev-btn"
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-ics-white" />
            </button>
            <div className="flex gap-2">
              {successStories.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentIndex ? "bg-ics-orange" : "bg-white/30"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={nextSlide}
              data-testid="carousel-next-btn"
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-ics-white" />
            </button>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-ics-gray mb-4">Want access to high-conviction market calls?</p>
          <a
            href={PATREON_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="success-stories-patreon-btn"
            className="inline-flex items-center gap-2 bg-ics-orange hover:bg-ics-orange-hover text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:scale-105"
          >
            Join ICS Patreon
            <Trophy className="w-5 h-5" />
          </a>
        </div>

        <p className="text-center text-xs text-ics-gray mt-8 max-w-2xl mx-auto">
          Past performance is not indicative of future results. These are educational examples only.
        </p>
      </div>

      {/* Modal for full-size image */}
      {isModalOpen && modalImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={closeModal}
        >
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="modal-close-btn"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="max-w-4xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <img
              src={modalImage.imageUrl}
              alt={modalImage.title}
              className="w-full h-auto rounded-lg"
            />
            <div className="mt-4 text-center">
              <h3 className="text-white font-heading text-xl font-semibold">{modalImage.title}</h3>
              <p className="text-ics-gray mt-2">{modalImage.description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SuccessStoriesSection;
